// LLM Streaming API — stored-prompt streams go through dash-api so they pick up
// JWT / AWP headers. dash-api `/api/llm/streaming` forwards raw OpenAI Responses
// chunks for promptId mode (same event types this parser already understands).
import {getApiBase} from '../config/apiBase';
import {fetchWithAwpHeaders} from './awpFetch';

const EventTypes = {
    RESPONSE_CREATED: 'response.created',
    RESPONSE_IN_PROGRESS: 'response.in_progress',
    RESPONSE_COMPLETED: 'response.completed',
    RESPONSE_FAILED: 'response.failed',
    RESPONSE_CANCELLED: 'response.cancelled',
    OUTPUT_TEXT_DELTA: 'response.output_text.delta',
    OUTPUT_TEXT_DONE: 'response.output_text.done',
    REASONING_SUMMARY_PART_ADDED: 'response.reasoning_summary_part.added',
    REASONING_SUMMARY_PART_DONE: 'response.reasoning_summary_part.done',
    REASONING_SUMMARY_TEXT_DELTA: 'response.reasoning_summary_text.delta',
    REASONING_SUMMARY_TEXT_DONE: 'response.reasoning_summary_text.done',
    ERROR: 'error'
};

function parseJSONStream(buffer) {
    const events = [];
    let inString = false;
    let escaped = false;
    let depth = 0;
    let start = -1;
    let lastConsumed = 0;

    for (let i = 0; i < buffer.length; i += 1) {
        const ch = buffer[i];

        if (inString) {
            if (!escaped && ch === '"') inString = false;
            escaped = !escaped && ch === '\\';
            continue;
        }

        if (ch === '"') {
            inString = true;
            escaped = false;
            continue;
        }

        if (ch === '{') {
            if (depth === 0) start = i;
            depth += 1;
        } else if (ch === '}') {
            if (depth > 0) depth -= 1;
            if (depth === 0 && start !== -1) {
                const jsonStr = buffer.slice(start, i + 1).trim();
                try {
                    const cleaned = jsonStr.startsWith('data:') ? jsonStr.replace(/^data:\s*/, '') : jsonStr;
                    const parsed = JSON.parse(cleaned);
                    events.push(parsed);
                    console.debug('[llmStream] parsed event type', parsed.type);
                } catch (e) {
                    console.debug('[llmStream] parse error', e, jsonStr);
                }
                lastConsumed = i + 1;
                start = -1;
            }
        }
    }

    const remaining = start === -1 ? buffer.slice(lastConsumed) : buffer.slice(start);
    return {events, remaining};
}

function createStreamingState() {
    return {
        outputText: '',
        reasoningSteps: [],
        currentReasoningText: '',
        currentSummaryIndex: -1,
        responseId: null,
        status: 'pending',
        error: null
    };
}

function handleStreamEvent(event, state, onEvent) {
    if (!event?.type) {
        // dash-api sendErrorEvent emits {error: "..."} without an OpenAI type.
        if (event?.error) {
            state.status = 'failed';
            state.error = typeof event.error === 'string' ? {message: event.error} : event.error;
            onEvent?.({type: 'error', error: state.error});
        }
        return;
    }

    switch (event.type) {
        case EventTypes.RESPONSE_CREATED:
            state.responseId = event.response?.id;
            state.status = 'created';
            onEvent?.({type: 'created', responseId: state.responseId});
            break;
        case EventTypes.RESPONSE_IN_PROGRESS:
            state.status = 'in_progress';
            onEvent?.({type: 'started'});
            break;
        case EventTypes.RESPONSE_COMPLETED:
            state.status = 'completed';
            if (state.currentReasoningText) {
                state.reasoningSteps.push(state.currentReasoningText);
                state.currentReasoningText = '';
            }
            if (!state.outputText && event.response?.output?.length) {
                const firstOutput = event.response.output[0];
                const textParts = firstOutput?.content?.map(c => c?.text || '').filter(Boolean).join('');
                if (textParts) state.outputText = textParts;
            }
            onEvent?.({type: 'completed', outputText: state.outputText, reasoningSteps: [...state.reasoningSteps]});
            break;
        case EventTypes.RESPONSE_FAILED:
            state.status = 'failed';
            state.error = event.response?.error || {message: 'Response failed'};
            onEvent?.({type: 'error', error: state.error});
            break;
        case EventTypes.RESPONSE_CANCELLED:
            state.status = 'cancelled';
            onEvent?.({type: 'cancelled'});
            break;

        case EventTypes.REASONING_SUMMARY_PART_ADDED:
            if (state.currentReasoningText) {
                state.reasoningSteps.push(state.currentReasoningText);
            }
            state.currentReasoningText = '';
            state.currentSummaryIndex = event.summary_index ?? state.currentSummaryIndex + 1;
            onEvent?.({
                type: 'reasoning_started',
                stepIndex: state.currentSummaryIndex,
                reasoningSteps: [...state.reasoningSteps]
            });
            break;
        case EventTypes.REASONING_SUMMARY_TEXT_DELTA:
            state.currentReasoningText += event.delta || '';
            onEvent?.({
                type: 'reasoning',
                delta: event.delta || '',
                currentStepText: state.currentReasoningText,
                stepIndex: event.summary_index ?? state.currentSummaryIndex,
                reasoningSteps: [...state.reasoningSteps, state.currentReasoningText].filter(Boolean),
                outputText: state.outputText
            });
            break;
        case EventTypes.REASONING_SUMMARY_TEXT_DONE:
        case EventTypes.REASONING_SUMMARY_PART_DONE:
            if (state.currentReasoningText) {
                state.reasoningSteps.push(state.currentReasoningText);
                state.currentReasoningText = '';
            }
            state.currentSummaryIndex = -1;
            onEvent?.({
                type: 'reasoning_done',
                reasoningSteps: [...state.reasoningSteps],
                outputText: state.outputText
            });
            break;

        case EventTypes.OUTPUT_TEXT_DELTA:
            state.outputText += event.delta || '';
            onEvent?.({
                type: 'output',
                delta: event.delta || '',
                outputText: state.outputText,
                reasoningSteps: state.currentReasoningText ? [...state.reasoningSteps, state.currentReasoningText] : [...state.reasoningSteps]
            });
            break;
        case EventTypes.OUTPUT_TEXT_DONE:
            if (event.output_text?.length) {
                const joined = event.output_text.map(p => p?.text || '').join('');
                if (joined) state.outputText = joined;
            }
            onEvent?.({type: 'output_done', outputText: state.outputText, reasoningSteps: [...state.reasoningSteps]});
            break;

        case EventTypes.ERROR:
            state.error = event.error || {message: 'Unknown error'};
            onEvent?.({type: 'error', error: state.error});
            break;
        default:
            console.debug('[llmStream] unhandled event', event.type);
    }
}

async function processStream(reader, state, onEvent, signal) {
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
        while (true) {
            if (signal?.aborted) {
                try {
                    await reader.cancel();
                } catch (_) {
                }
                state.status = 'cancelled';
                onEvent?.({type: 'cancelled'});
                break;
            }

            const {value, done} = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, {stream: true});
            const {events, remaining} = parseJSONStream(buffer);
            buffer = remaining;
            for (const ev of events) handleStreamEvent(ev, state, onEvent);
        }

        buffer += decoder.decode();
        if (buffer.trim()) {
            const {events} = parseJSONStream(buffer);
            for (const ev of events) handleStreamEvent(ev, state, onEvent);
        }

        if (state.currentReasoningText) {
            state.reasoningSteps.push(state.currentReasoningText);
            state.currentReasoningText = '';
        }
    } finally {
        reader.releaseLock();
    }
}

export async function callLLMStreaming(promptId, messages, onEvent, signal) {
    const requestBody = {promptId, messages, options: {}};
    const state = createStreamingState();
    const url = `${getApiBase()}/llm/streaming`;

    const response = await fetchWithAwpHeaders(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
        },
        body: JSON.stringify(requestBody),
        signal
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`LLM Streaming API request failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    if (!response.body?.getReader) {
        const text = await response.text();
        const {events} = parseJSONStream(text);
        events.forEach(ev => handleStreamEvent(ev, state, onEvent));
        return {output: state.outputText, reasoningSteps: state.reasoningSteps};
    }

    const reader = response.body.getReader();
    await processStream(reader, state, onEvent, signal);

    if (state.status === 'failed' && state.error) throw state.error;
    return {output: state.outputText, reasoningSteps: state.reasoningSteps};
}

export {EventTypes};
